/**
 * Jours fériés français, calculés plutôt qu'énumérés.
 *
 * Une liste écrite en dur serait à retenir chaque année, et un calendrier qui se trompe sur
 * 2030 est pire que pas de fériés du tout. Les quatre mobiles découlent tous de Pâques, dont
 * la date se calcule pour n'importe quelle année ; les huit autres sont fixes.
 *
 * Périmètre : **France métropolitaine**. Le Vendredi saint et le 26 décembre, fériés en
 * Alsace-Moselle, n'y sont pas — les griser partout serait faux pour la quasi-totalité des
 * comptes.
 */

/** Date au format `YYYY-MM-DD`, lue sur le fuseau local. */
const enIso = (d) => {
  const mois = String(d.getMonth() + 1).padStart(2, '0');
  const jour = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mois}-${jour}`;
};

/**
 * Dimanche de Pâques, par l'algorithme de Meeus/Butcher.
 *
 * Purement arithmétique — aucune table, aucune dépendance — et valable pour tout le
 * calendrier grégorien. Les noms de variables sont ceux de l'algorithme : les renommer
 * rendrait la comparaison avec la référence impossible.
 */
function paques(annee) {
  const a = annee % 19;
  const b = Math.floor(annee / 100);
  const c = annee % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mois = Math.floor((h + l - 7 * m + 114) / 31);
  const jour = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(annee, mois - 1, jour);
}

/** Une date décalée de `n` jours. Passe les changements de mois et d'année d'elle-même. */
const decale = (date, n) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);

/**
 * Les onze jours fériés d'une année.
 *
 * @param {number} annee
 * @returns {Map<string, string>} `'YYYY-MM-DD'` → libellé
 */
export function joursFeries(annee) {
  const dimanchePaques = paques(annee);

  const jours = [
    [new Date(annee, 0, 1), 'Jour de l\'An'],
    [decale(dimanchePaques, 1), 'Lundi de Pâques'],
    [new Date(annee, 4, 1), 'Fête du Travail'],
    [new Date(annee, 4, 8), 'Victoire 1945'],
    [decale(dimanchePaques, 39), 'Ascension'],
    [decale(dimanchePaques, 50), 'Lundi de Pentecôte'],
    [new Date(annee, 6, 14), 'Fête nationale'],
    [new Date(annee, 7, 15), 'Assomption'],
    [new Date(annee, 10, 1), 'Toussaint'],
    [new Date(annee, 10, 11), 'Armistice 1918'],
    [new Date(annee, 11, 25), 'Noël']
  ];

  return new Map(jours.map(([date, libelle]) => [enIso(date), libelle]));
}

/**
 * Fériés couvrant une grille de calendrier, qui déborde sur les mois voisins.
 *
 * Une grille de décembre montre les premiers jours de janvier : se limiter à l'année du mois
 * affiché laisserait le Jour de l'An en jour ordinaire. Deux années suffisent, la grille ne
 * dépassant jamais six semaines.
 */
export function feriesDeLaPeriode(debut, fin) {
  const feries = new Map();
  for (let annee = debut.getFullYear(); annee <= fin.getFullYear(); annee++) {
    for (const [cle, libelle] of joursFeries(annee)) feries.set(cle, libelle);
  }
  return feries;
}
